import React, { useCallback } from "react";
import { Text, View, Link } from "@react-pdf/renderer";
import type { Style } from "@react-pdf/types";
import { styles, spacing } from "components/Resume/ResumePDF/styles";
import { DEBUG_RESUME_PDF_FLAG } from "lib/constants";
import { DEFAULT_FONT_COLOR } from "lib/redux/settingsSlice";
import {
  ResumePDFLink,
  ResumePDFText,
} from "components/Resume/ResumePDF/common";
import { handleClick } from "lib/utils/navigator";
import { useFontSize } from "components/Resume/ResumePDF/FontSizeContext";
import { useHighlight, shouldHighlightText, isHighlightTarget, getMergedHighlightStyle } from "components/Resume/HighlightContext";
import { useMistakeAnalysis, isWordMistake, isSentenceMistake, isSingleWordMistake } from "components/Resume/MistakeAnalysisContext";

const MISTAKE_TEXT_COLOR = "#dc2626";

export const TemplateEightPDFSection = ({
  themeColor,
  heading,
  style = {},
  children,
}: {
  themeColor?: string;
  heading?: string;
  style?: Style;
  children: React.ReactNode;
}) => {
  const { dynamicStyles } = useFontSize();
  const handleClickCallback = useCallback(() => handleClick(heading), [heading]);
  
  return (
    <div
      onClick={handleClickCallback}
      style={{
        cursor: "pointer",
      }}
    >
      {/* Wrap entire section to prevent orphaning */}
      <View wrap={false} style={{ ...styles.flexCol, gap: spacing["2"] }}>
        {heading && (
          <View style={{ ...styles.flexCol, gap: spacing["1"] }}>
            <Text
              style={{
                ...dynamicStyles.heading,
                fontWeight: "bold",
                letterSpacing: "0.3pt",
                color: "white",
              }}
              debug={DEBUG_RESUME_PDF_FLAG}
            >
              {heading}
            </Text>
            {themeColor && (
              <View
                style={{
                  height: "1.25pt",
                  width: "100%",
                  backgroundColor: "white",
                  marginRight: spacing["3.5"],
                }}
                debug={DEBUG_RESUME_PDF_FLAG}
              />
            )}
          </View>
        )}
        {children}
      </View>
    </div>
  );
};

//  orphan prevention 
export const TemplateEightEnhancedSection = ({
  themeColor,
  heading,
  children,
  textColor = "white", // Default white text for colored background
}: {
  themeColor?: string;
  heading?: string;
  children?: React.ReactNode;
  textColor?: string;
}) => {
  const { dynamicStyles } = useFontSize();
  const handleClickCallback = useCallback(() => handleClick(heading), [heading]);
  
  return (
    <div
      onClick={handleClickCallback}
      style={{
        cursor: "pointer"
      }}
    >
      {/* Wrap entire section to prevent orphaning */}
      <View wrap={false} style={{ ...styles.flexCol, gap: spacing["1"] }}>
        {heading && (
          <View style={{ ...styles.flexCol, gap: spacing["0.5"] }}>
            <Text
              style={{
                ...dynamicStyles.heading,
                fontWeight: "bold",
                letterSpacing: "0.3pt",
                color: textColor,
              }}
              debug={DEBUG_RESUME_PDF_FLAG}
            >
              {heading}
            </Text>
            {themeColor && (
                <View
                  style={{
                    height: "1.25pt",
                    width: "100%",
                    backgroundColor: "white",
                    marginRight: spacing["3.5"],
                  }}
                  debug={DEBUG_RESUME_PDF_FLAG}
                />
              )}
            </View>
          )}
        {children}
      </View>
    </div>
  );
};

// section with subsections  in left column 
export const TemplateEightLeftColumnSectionWithSubsections = ({
  themeColor,
  heading,
  children,
}: {
  themeColor?: string;
  heading?: string;
  children: React.ReactNode;
}) => {
  const { dynamicStyles } = useFontSize();
  const handleClickCallback = useCallback(() => handleClick(heading), [heading]);
  const childrenArray = React.Children.toArray(children);
  
  if (childrenArray.length === 0) {
    return null;
  }

  // First child gets grouped with heading to prevent orphans
  const [firstChild, ...restChildren] = childrenArray;

  return (
    <div
      onClick={handleClickCallback}
      style={{
        ...styles.flexCol,
        marginTop: spacing["3"],
        cursor: "pointer"
      }}
    >
      {/* Heading + first subsection container to prevent orphans */}
      <View wrap={false} style={{ ...styles.flexCol }}>
        {heading && (
          <View style={{ ...styles.flexCol, gap: spacing["0.5"] }}>
            <Text
              style={{
                ...dynamicStyles.heading,
                fontWeight: "bold",
                letterSpacing: "0.3pt",
                color: "white",
              }}
              debug={DEBUG_RESUME_PDF_FLAG}
            >
              {heading}
            </Text>
            {themeColor && (
              <View
                style={{
                  height: "1.25pt",
                  width: "100%",
                  backgroundColor: "white",
                  marginRight: spacing["3.5"],
                }}
                debug={DEBUG_RESUME_PDF_FLAG}
              />
            )}
          </View>
        )}
        <View style={{ ...styles.flexCol, marginTop: spacing["1"] }}>
          {firstChild}
        </View>
      </View>
      
      {/* Rest of the subsections */}
      {restChildren.length > 0 && (
        <View style={{ ...styles.flexCol }}>
          {restChildren}
        </View>
      )}
    </div>
  );
};

//  section with subsections  in right column 
export const TemplateEightRightColumnSectionWithSubsections = ({
  themeColor,
  heading,
  children,
}: {
  themeColor?: string;
  heading?: string;
  children: React.ReactNode;
}) => {
  const { dynamicStyles } = useFontSize();
  const handleClickCallback = useCallback(() => handleClick(heading), [heading]);
  const childrenArray = React.Children.toArray(children);
  
  if (childrenArray.length === 0) {
    return null;
  }

  // First child  grouped with heading to prevent orphans
  const [firstChild, ...restChildren] = childrenArray;

  return (
    <div
      onClick={handleClickCallback}
      style={{
        ...styles.flexCol,
        marginTop: spacing["3"],
        cursor: "pointer"
      }}
    >
      {/* Heading + first subsection container to prevent orphans */}
      <View wrap={false} style={{ ...styles.flexCol }}>
        {heading && (
          <View style={{ ...styles.flexCol, gap: spacing["0.5"] }}>
            <Text
              style={{
                ...dynamicStyles.heading,
                fontWeight: "bold",
                letterSpacing: "0.3pt",
                color: "black",
              }}
              debug={DEBUG_RESUME_PDF_FLAG}
            >
              {heading}
            </Text>
            {themeColor && (
              <View
                style={{
                  height: "1.25pt",
                  width: "100%",
                  backgroundColor: themeColor,
                  marginRight: spacing["3.5"],
                }}
                debug={DEBUG_RESUME_PDF_FLAG}
              />
            )}
          </View>
        )}
        <View style={{ ...styles.flexCol, marginTop: spacing["1"] }}>
          {firstChild}
        </View>
      </View>
      
      {/* Rest of the subsections */}
      {restChildren.length > 0 && (
        <View style={{ ...styles.flexCol }}>
          {restChildren}
        </View>
      )}
    </div>
  );
};

/**
 * Render node with highlighting support for white text (left column)
 */
const renderNodeWithHighlightWhiteText = (
  node: Node,
  currentSection: string,
  currentItemIndex: number | null,
  enableMistakeHighlight: boolean = false,
  mistakeAnalysis?: ReturnType<typeof useMistakeAnalysis>
): React.ReactNode => {
  const highlight = useHighlight();
  
  const isTarget = isHighlightTarget(currentSection, currentItemIndex, highlight);
  
  if (node.nodeType === Node.ELEMENT_NODE) {
    const element = node as Element;
    const textContent = element.textContent || "";
    const nodeName = element.nodeName.toLowerCase();
    
    const shouldHighlight = isTarget && 
      highlight.enabled && 
      highlight.mode === 'sentence' && 
      shouldHighlightText(textContent, highlight.texts, highlight.mode);
    
    switch (nodeName) {
      case "p":
        return (
          <View key={Math.random()}>
            <ResumePDFText 
              style={{ 
                lineHeight: 1.3,
                flexGrow: 1,
                ...(shouldHighlight ? {
                  backgroundColor: highlight.style.backgroundColor,
                  color: highlight.style.color || highlight.style.textDecorationColor,
                  fontWeight: highlight.style.fontWeight as any,
                  textDecoration: highlight.style.textDecoration as any,
                  textDecorationStyle: highlight.style.textDecorationStyle as any,
                  textDecorationColor: highlight.style.textDecorationColor as any,
                } : {})
              }}
              themeColor="white"
            >
              {parseAndTransformHTMLWithHighlightWhiteText(element.innerHTML, currentSection, currentItemIndex, enableMistakeHighlight, mistakeAnalysis)}
              <br />
            </ResumePDFText>
          </View>
        );
        
      case "ol":
        return (
          <View key={Math.random()} style={{ ...styles.flexCol }}>
            {Array.from(element.childNodes).map((child, index) =>
              child.nodeName.toLowerCase() === "li" ? (
                <View key={index} style={{ ...styles.flexRow }}>
                  <ResumePDFText
                    style={{
                      paddingLeft: spacing["2"],
                      paddingRight: spacing["2"],
                      lineHeight: 1.3,
                    }}
                    bold={true}
                    themeColor="white"
                  >
                    {"•"}
                  </ResumePDFText>
                  <ResumePDFText style={{ lineHeight: 1.3, flexGrow: 1, flexBasis: 0 }} themeColor="white">
                    {parseAndTransformHTMLWithHighlightWhiteText((child as Element).innerHTML, currentSection, currentItemIndex, enableMistakeHighlight, mistakeAnalysis)}
                  </ResumePDFText>
                </View>
              ) : null
            )}
          </View>
        );
        
      case "li":
        const liShouldHighlight = isTarget && 
          highlight.enabled && 
          highlight.mode === 'sentence' && 
          shouldHighlightText(textContent, highlight.texts, highlight.mode);
          
        return (
          <View key={Math.random()} style={{ ...styles.flexRow }}>
            <ResumePDFText
              style={{
                paddingLeft: spacing["2"],
                paddingRight: spacing["2"],
                lineHeight: 1.3,
              }}
              bold={true}
              themeColor="white"
            >
              {"•"}
            </ResumePDFText>
            <ResumePDFText
              style={{
                lineHeight: 1.3,
                flexGrow: 1,
                flexBasis: 0,
                ...(liShouldHighlight ? {
                  backgroundColor: highlight.style.backgroundColor,
                  color: highlight.style.color || highlight.style.textDecorationColor,
                  fontWeight: highlight.style.fontWeight as any,
                  textDecoration: highlight.style.textDecoration as any,
                  textDecorationStyle: highlight.style.textDecorationStyle as any,
                  textDecorationColor: highlight.style.textDecorationColor as any,
                } : {} )
              }}
              themeColor="white"
            >
              {parseAndTransformHTMLWithHighlightWhiteText(element.innerHTML, currentSection, currentItemIndex, enableMistakeHighlight, mistakeAnalysis)}
            </ResumePDFText>
          </View>
        );
        
      default:
        return null;
    }
  }
  return null;
};

/**
 * Parse and transform HTML with highlighting support for white text
 */
const parseAndTransformHTMLWithHighlightWhiteText = (
  htmlString: string,
  currentSection: string,
  currentItemIndex: number | null,
  enableMistakeHighlight: boolean = false,
  mistakeAnalysis?: ReturnType<typeof useMistakeAnalysis>
): React.ReactNode[] => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, "text/html");
  const transformedChildren: React.ReactNode[] = [];
  const highlight = useHighlight();

  const isTarget = isHighlightTarget(currentSection, currentItemIndex, highlight);

  const transformNode = (node: Node, existingStyles: { bold?: boolean; italic?: boolean; underline?: boolean } = {}) => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as HTMLElement;
      const nodeName = element.nodeName.toLowerCase();

      let newStyles = { ...existingStyles };
      
      switch (nodeName) {
        case "strong":
        case "b":
          newStyles.bold = true;
          return (
            <>
              {Array.from(element.childNodes).map((child, idx) => (
                <React.Fragment key={`${Math.random()}-${idx}`}>
                  {transformNode(child, newStyles)}
                </React.Fragment>
              ))}
            </>
          );
          
        case "em":
        case "i":
          newStyles.italic = true;
          return (
            <>
              {Array.from(element.childNodes).map((child, idx) => (
                <React.Fragment key={`${Math.random()}-${idx}`}>
                  {transformNode(child, newStyles)}
                </React.Fragment>
              ))}
            </>
          );
          
        case "u":
          newStyles.underline = true;
          return (
            <>
              {Array.from(element.childNodes).map((child, idx) => (
                <React.Fragment key={`${Math.random()}-${idx}`}>
                  {transformNode(child, newStyles)}
                </React.Fragment>
              ))}
            </>
          );
          
        case "a":
          const href = element.getAttribute("href");
          return (
            <ResumePDFLink
              isPDF={DEBUG_RESUME_PDF_FLAG as true}
              src={href || "#"}
              key={Math.random()}
            >
              <ResumePDFText themeColor="blue" underline={true}>
                {Array.from(element.childNodes).map((child, idx) => (
                  <React.Fragment key={`${Math.random()}-${idx}`}>
                    {transformNode(child, { ...newStyles, underline: true })}
                  </React.Fragment>
                ))}
              </ResumePDFText>
            </ResumePDFLink>
          );
          
        case "span":
          return (
            <>
              {Array.from(element.childNodes).map((child, idx) => (
                <React.Fragment key={`${Math.random()}-${idx}`}>
                  {transformNode(child, newStyles)}
                </React.Fragment>
              ))}
            </>
          );
          
        default:
          return (
            <>
              {Array.from(element.childNodes).map((child, idx) => (
                <React.Fragment key={`${Math.random()}-${idx}`}>
                  {transformNode(child, newStyles)}
                </React.Fragment>
              ))}
            </>
          );
      }
    } else if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent || "";
      
      if (!text.trim()) return text;
      
      // Check if entire text is a sentence mistake
      const isSentenceError = enableMistakeHighlight && mistakeAnalysis && currentSection && currentItemIndex !== null && 
        isSentenceMistake(text, currentSection, currentItemIndex, mistakeAnalysis.mistakeData);

      if (isSentenceError) {
        // Text is a sentence mistake - handle hover interactions
        const isWordHover = isTarget && highlight.enabled && highlight.mode === 'word' && highlight.texts.length > 0;
        const isSentenceHover = isTarget && highlight.enabled && highlight.mode === 'sentence' && highlight.texts.length > 0 &&
          shouldHighlightText(text, highlight.texts, 'sentence');

        if (isWordHover) {
          // Sentence mistake + word hover
          return renderTextWithWordHighlightsWhiteText(
            text, 
            highlight, 
            existingStyles,
            currentSection,
            currentItemIndex,
            mistakeAnalysis,
            true,  // isHoverTarget
            enableMistakeHighlight,
            true   // forceRedText
          );
        } else if (isSentenceHover) {
          // Sentence mistake + sentence hover
          const mergedStyle = getMergedHighlightStyle(existingStyles, highlight.style, true);
          return (
            <ResumePDFText key={Math.random()} style={{ ...mergedStyle, color: MISTAKE_TEXT_COLOR }} themeColor="white">
              {text}
            </ResumePDFText>
          );
        } else {
          // Sentence mistake, no hover
          return (
            <ResumePDFText key={Math.random()} style={{ color: MISTAKE_TEXT_COLOR }} themeColor="white">
              {text}
            </ResumePDFText>
          );
        }
      }

      // Check if text needs word-level mistake processing
      const needsWordProcessing = enableMistakeHighlight && mistakeAnalysis && currentSection && currentItemIndex !== null;
      
      if (!isTarget || !highlight.enabled) {
        if (needsWordProcessing) {
          // Word-level mistake detection without hover
          return renderTextWithWordHighlightsWhiteText(
            text,
            highlight,
            existingStyles,
            currentSection,
            currentItemIndex,
            mistakeAnalysis,
            false,  // isHoverTarget
            enableMistakeHighlight,
            false   // forceRedText
          );
        }
        
        if (existingStyles.bold || existingStyles.italic || existingStyles.underline) {
          return (
            <ResumePDFText
              key={Math.random()}
              bold={existingStyles.bold}
              italic={existingStyles.italic}
              underline={existingStyles.underline}
              themeColor="white"
            >
              {text}
            </ResumePDFText>
          );
        }
        return text;
      }

      if (highlight.mode === 'word' && highlight.texts.length > 0) {
        return renderTextWithWordHighlightsWhiteText(
          text, 
          highlight, 
          existingStyles,
          currentSection,
          currentItemIndex,
          mistakeAnalysis,
          true,  // isHoverTarget
          enableMistakeHighlight,
          false  // forceRedText
        );
      }

      if (highlight.mode === 'sentence' && highlight.texts.length > 0) {
        const shouldHighlight = shouldHighlightText(text, highlight.texts, 'sentence');
        
        if (needsWordProcessing) {
          // Sentence hover + word-level mistakes
          return renderTextWithWordHighlightsWhiteText(
            text,
            highlight,
            existingStyles,
            currentSection,
            currentItemIndex,
            mistakeAnalysis,
            shouldHighlight,  // isHoverTarget
            enableMistakeHighlight,
            false  // forceRedText
          );
        }
        
        return (
          <ResumePDFText
            key={Math.random()}
            style={getMergedHighlightStyle(existingStyles, highlight.style, shouldHighlight)}
            themeColor="white"
          >
            {text}
          </ResumePDFText>
        );
      }

      if (existingStyles.bold || existingStyles.italic || existingStyles.underline) {
        return (
          <ResumePDFText
            key={Math.random()}
            bold={existingStyles.bold}
            italic={existingStyles.italic}
            underline={existingStyles.underline}
            themeColor="white"
          >
            {text}
          </ResumePDFText>
        );
      }
      
      return text;
    }
    
    return null;
  };

  doc.body.childNodes.forEach((node) => {
    const transformed = transformNode(node);
    if (transformed) {
      transformedChildren.push(transformed);
    }
  });

  return transformedChildren;
};

/**
 * Render text with word-level highlights for white text
 */
const renderTextWithWordHighlightsWhiteText = (
  text: string,
  highlight: ReturnType<typeof useHighlight>,
  existingStyles: { bold?: boolean; italic?: boolean; underline?: boolean },
  currentSection?: string,
  currentItemIndex?: number | null,
  mistakeAnalysis?: ReturnType<typeof useMistakeAnalysis>,
  isHoverTarget: boolean = false,
  enableMistakeHighlight: boolean = false,
  forceRedText: boolean = false
): React.ReactNode => {
  try {
    const words = highlight.texts
      .map(w => (w || "").trim())
      .filter(Boolean)
      .map(w => {
        const escaped = w.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        return escaped;
      })
      .join("|");

    if (!words) {
      // No hover highlights, check for mistakes only
      if (enableMistakeHighlight && mistakeAnalysis && currentSection && currentItemIndex !== null && !forceRedText) {
        const textWords = text.split(/\s+/).filter(Boolean);
        const hasAnyMistake = textWords.some(word =>
          isSingleWordMistake(word, currentSection, currentItemIndex ?? null, mistakeAnalysis.mistakeData)
        );

        if (hasAnyMistake) {
          return (
            <>
              {textWords.map((word, i) => {
                const isMistake = isSingleWordMistake(word, currentSection, currentItemIndex ?? null, mistakeAnalysis.mistakeData);
                const mergedStyle = isMistake
                  ? { ...existingStyles, color: MISTAKE_TEXT_COLOR }
                  : existingStyles;
                
                return (
                  <React.Fragment key={i}>
                    {i > 0 && " "}
                    {(mergedStyle.bold || mergedStyle.italic || mergedStyle.underline || isMistake) ? (
                      <ResumePDFText
                        bold={mergedStyle.bold}
                        italic={mergedStyle.italic}
                        underline={mergedStyle.underline}
                        style={isMistake ? { color: MISTAKE_TEXT_COLOR } : {}}
                        themeColor="white"
                      >
                        {word}
                      </ResumePDFText>
                    ) : (
                      word
                    )}
                  </React.Fragment>
                );
              })}
            </>
          );
        }
      }
      
      if (forceRedText) {
        return (
          <ResumePDFText key={Math.random()} style={{ color: MISTAKE_TEXT_COLOR }} themeColor="white">
            {text}
          </ResumePDFText>
        );
      }
      
      if (existingStyles.bold || existingStyles.italic || existingStyles.underline) {
        return (
          <ResumePDFText
            key={Math.random()}
            bold={existingStyles.bold}
            italic={existingStyles.italic}
            underline={existingStyles.underline}
            themeColor="white"
          >
            {text}
          </ResumePDFText>
        );
      }
      return text;
    }

    const re = new RegExp(`\\b(${words})\\b`, "giu");
    const parts: Array<{ text: string; match: boolean }> = [];
    let lastIndex = 0;
    let match;

    while ((match = re.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push({ text: text.slice(lastIndex, match.index), match: false });
      }
      parts.push({ text: match[0], match: true });
      lastIndex = re.lastIndex;
    }

    if (lastIndex < text.length) {
      parts.push({ text: text.slice(lastIndex), match: false });
    }

    return (
      <>
        {parts.map((part, i) => {
          const baseStyle = getMergedHighlightStyle(existingStyles, highlight.style, part.match && isHoverTarget);
          
          // Check for word-level mistakes
          const isMistakeWord = !forceRedText && enableMistakeHighlight && mistakeAnalysis && currentSection && currentItemIndex !== null &&
            isSingleWordMistake(part.text, currentSection, currentItemIndex ?? null, mistakeAnalysis.mistakeData);
          
          let finalStyle = baseStyle;
          if (forceRedText) {
            finalStyle = { ...baseStyle, color: MISTAKE_TEXT_COLOR };
          } else if (isMistakeWord) {
            // Mistake word: combine hover highlight (if any) with red text
            if (part.match && isHoverTarget) {
              // Hover + mistake = yellow background + red text
              finalStyle = { ...baseStyle, color: MISTAKE_TEXT_COLOR };
            } else {
              // Mistake only = red text
              finalStyle = { ...baseStyle, color: MISTAKE_TEXT_COLOR };
            }
          }
          
          return (
            <ResumePDFText key={i} style={finalStyle} themeColor="white">
              {part.text}
            </ResumePDFText>
          );
        })}
      </>
    );
  } catch (error) {
    if (forceRedText) {
      return (
        <ResumePDFText key={Math.random()} style={{ color: MISTAKE_TEXT_COLOR }} themeColor="white">
          {text}
        </ResumePDFText>
      );
    }
    
    if (existingStyles.bold || existingStyles.italic || existingStyles.underline) {
      return (
        <ResumePDFText
          key={Math.random()}
          bold={existingStyles.bold}
          italic={existingStyles.italic}
          underline={existingStyles.underline}
          themeColor="white"
        >
          {text}
        </ResumePDFText>
      );
    }
    return text;
  }
};

export const ResumePDFDescriptionWithWhiteFont = ({
  items,
  enableHighlight = false,
  enableMistakeHighlight = false,
  section = 'experience',
  itemIndex = 0
}: {
  items: string[];
  enableHighlight?: boolean;
  enableMistakeHighlight?: boolean;
  section?: string;
  itemIndex?: number | null;
}) => {
  const mistakeAnalysis = useMistakeAnalysis();
  
  // Check if items is defined and has at least one element
  if (!items || items.length === 0) {
    return null; // or render a placeholder, like <p>No descriptions available</p>
  }
  const newDescription = items.join("");
  const descriptionNodes = extractNodesFromHtml(newDescription);

  return (
    <View wrap={false}>
      {descriptionNodes.map((node, idx) => (
        <React.Fragment key={idx}>
          {enableHighlight ? renderNodeWithHighlightWhiteText(node, section, itemIndex, enableMistakeHighlight, mistakeAnalysis) : renderNode(node)}
        </React.Fragment>
      ))}
    </View>
  );
};

const parseAndTransformHTML = (htmlString: string): React.ReactNode[] => {
  // Create a temporary DOM element to parse the HTML string
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, "text/html");
  const transformedChildren: React.ReactNode[] = [];

  // Recursive function to process and transform HTML nodes
  const transformNode = (node: Node) => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as HTMLElement;

      switch (element.nodeName.toLowerCase()) {
        case "strong":
          return (
            <ResumePDFText key={Math.random()} bold={true}>
              {element.textContent}
            </ResumePDFText>
          );
        case "em":
          return (
            <ResumePDFText key={Math.random()} italic={true}>
              {element.textContent}
            </ResumePDFText>
          );
        case "u":
          return (
            <ResumePDFText
              key={Math.random()}
              underline={true}
            >
              {element.textContent}
            </ResumePDFText>
          );
        case "a":
          const href = element.getAttribute("href");
          return (
            <ResumePDFLink
              isPDF={DEBUG_RESUME_PDF_FLAG as true}
              src={href || "#"}
              key={Math.random()}
            >
              <ResumePDFText themeColor="blue" underline={true}>
                {element.textContent}
              </ResumePDFText>
            </ResumePDFLink>
          );
        case "span":
          // Skip span elements
          return null;
        case "p":
          return (
            <View key={Math.random()}>
              <ResumePDFText>
                {Array.from(element.childNodes).map(transformNode)}
              </ResumePDFText>
              ;
            </View>
          );
        case "li":
          return (
            <View key={Math.random()} style={{ flexDirection: "row" }}>
              <ResumePDFText
                style={{
                  paddingLeft: 4, // adjust spacing as needed
                  paddingRight: 4, // adjust spacing as needed
                  lineHeight: 1.3,
                }}
                bold={true}
              >
                {"•"}
              </ResumePDFText>
              <ResumePDFText
                style={{ lineHeight: 1.3, flexGrow: 1, flexBasis: 0 }}
              >
                {Array.from(element.childNodes).map(transformNode)}
              </ResumePDFText>
            </View>
          );
        case "ol":
          return (
            <View key={Math.random()} style={{ flexDirection: "column" }}>
              {Array.from(element.childNodes).map((child, index) =>
                transformNode(child)
              )}
            </View>
          );
        default:
          return null;
      }
    }

    if (node.nodeType === Node.TEXT_NODE) {
      return node.textContent;
    }

    return null;
  };

  Array.from(doc.body.childNodes).forEach((child) => {
    const transformedNode = transformNode(child);
    if (transformedNode) {
      transformedChildren.push(transformedNode);
    }
  });

  return transformedChildren;
};

const renderNode = (node: Node): React.ReactNode => {
  if (node.nodeType === Node.ELEMENT_NODE) {
    const element = node as Element;
    switch (element.nodeName.toLowerCase()) {
      case "p":
        // return <Text key={Math.random()}>{element.textContent}</Text>;
        return (
          <View>
            <ResumePDFText
              style={{ lineHeight: 1.3, flexGrow: 1 }}
              themeColor="white"
            >
              {parseAndTransformHTML(element.innerHTML)}
              <br />
            </ResumePDFText>
          </View>
        );
      case "ol":
        return (
          <View key={Math.random()} style={{ ...styles.flexCol }}>
            {Array.from(element.childNodes).map((child, index) =>
              child.nodeName.toLowerCase() === "li" ? (
                <View key={index} style={{ ...styles.flexRow }}>
                  <ResumePDFText
                    style={{
                      paddingLeft: spacing["1"], // Reduced from spacing["2"] for better density
                      paddingRight: spacing["1"], // Reduced from spacing["2"] for better density
                      lineHeight: "1.3",
                    }}
                    bold={true}
                    themeColor="white"
                  >
                    {"•"}
                  </ResumePDFText>
                  <ResumePDFText
                    style={{ lineHeight: "1.3", flexGrow: 1, flexBasis: 0 }}
                    themeColor="white"
                  >
                    {parseAndTransformHTML((child as Element).innerHTML)}
                  </ResumePDFText>
                </View>
              ) : null
            )}
          </View>
        );
      case "li":
        return (
          <View key={Math.random()} style={{ ...styles.flexRow }}>
            <ResumePDFText
              style={{
                paddingLeft: spacing["1"], // spacing["2"] 
                paddingRight: spacing["1"], //   spacing["2"] 
                lineHeight: 1.3,
              }}
              bold={true}
              themeColor="white"
            >
              {"•"}
            </ResumePDFText>
            <ResumePDFText
              style={{
                lineHeight: 1.3,
                flexGrow: 1,
                flexBasis: 0,
              }}
              themeColor="white"
            >
              {parseAndTransformHTML(element.innerHTML)}
            </ResumePDFText>
          </View>
        );
      default:
        return null;
    }
  }
  return null;
};

export const extractNodesFromHtml = (html: string) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const nodes = Array.from(doc.body.childNodes);
  return nodes;
};

//  right column 
export const TemplateEightRightColumnSection = ({
  themeColor,
  heading,
  children,
}: {
  themeColor?: string;
  heading?: React.ReactNode;
  children?: React.ReactNode;
}) => {
  const { dynamicStyles } = useFontSize();
  return (
    <View 
      style={{ 
        ...styles.flexCol, 
        gap: spacing["1"], 
        marginTop: spacing["3"], 
      }}
      wrap={false} // Prevent section heading from being orphaned
      debug={DEBUG_RESUME_PDF_FLAG}
    >
      {heading && (
        <View style={{ ...styles.flexCol, gap: spacing["0.5"] }}>
          <Text
            style={{
              ...dynamicStyles.heading,
              fontWeight: "bold",
              letterSpacing: "0.3pt",
              color: "black", // Black text for white background
            }}
            debug={DEBUG_RESUME_PDF_FLAG}
          >
            {heading}
          </Text>
          {themeColor && (
            <View
              style={{
                height: "1.25pt",
                width: "100%",
                backgroundColor: themeColor, 
                marginRight: spacing["3.5"],
              }}
              debug={DEBUG_RESUME_PDF_FLAG}
            />
          )}
        </View>
      )}
      {children && (
        <View
          style={{
            ...styles.flexCol,
            gap: spacing["1"], 
          }}
        >
          {children}
        </View>
      )}
    </View>
  );
};

/**
 * Render node with highlighting support for black text
 */
const renderNodeWithHighlightBlackText = (
  node: Node,
  currentSection: string,
  currentItemIndex: number | null,
  enableMistakeHighlight: boolean = false,
  mistakeAnalysis?: ReturnType<typeof useMistakeAnalysis>
): React.ReactNode => {
  const highlight = useHighlight();
  
  const isTarget = isHighlightTarget(currentSection, currentItemIndex, highlight);
  
  if (node.nodeType === Node.ELEMENT_NODE) {
    const element = node as Element;
    const textContent = element.textContent || "";
    const nodeName = element.nodeName.toLowerCase();
    
    const shouldHighlight = isTarget && 
      highlight.enabled && 
      highlight.mode === 'sentence' && 
      shouldHighlightText(textContent, highlight.texts, highlight.mode);
    
    switch (nodeName) {
      case "p":
        return (
          <View key={Math.random()}>
            <ResumePDFText 
              style={{ 
                lineHeight: 1.3,
                flexGrow: 1,
                ...(shouldHighlight ? {
                  backgroundColor: highlight.style.backgroundColor,
                  color: highlight.style.color || highlight.style.textDecorationColor,
                  fontWeight: highlight.style.fontWeight as any,
                  textDecoration: highlight.style.textDecoration as any,
                  textDecorationStyle: highlight.style.textDecorationStyle as any,
                  textDecorationColor: highlight.style.textDecorationColor as any,
                } : {})
              }}
              themeColor="black"
            >
              {parseAndTransformHTMLWithHighlightBlackText(element.innerHTML, currentSection, currentItemIndex, enableMistakeHighlight, mistakeAnalysis)}
              <br />
            </ResumePDFText>
          </View>
        );
        
      case "ol":
        return (
          <View key={Math.random()} style={{ ...styles.flexCol }}>
            {Array.from(element.childNodes).map((child, index) =>
              child.nodeName.toLowerCase() === "li" ? (
                <View key={index} style={{ ...styles.flexRow }}>
                  <ResumePDFText
                    style={{
                      paddingLeft: spacing["2"],
                      paddingRight: spacing["2"],
                      lineHeight: 1.3,
                    }}
                    bold={true}
                    themeColor="black"
                  >
                    {"•"}
                  </ResumePDFText>
                  <ResumePDFText style={{ lineHeight: 1.3, flexGrow: 1, flexBasis: 0 }} themeColor="black">
                    {parseAndTransformHTMLWithHighlightBlackText((child as Element).innerHTML, currentSection, currentItemIndex, enableMistakeHighlight, mistakeAnalysis)}
                  </ResumePDFText>
                </View>
              ) : null
            )}
          </View>
        );
        
      case "li":
        const liShouldHighlight = isTarget && 
          highlight.enabled && 
          highlight.mode === 'sentence' && 
          shouldHighlightText(textContent, highlight.texts, highlight.mode);
          
        return (
          <View key={Math.random()} style={{ ...styles.flexRow }}>
            <ResumePDFText
              style={{
                paddingLeft: spacing["2"],
                paddingRight: spacing["2"],
                lineHeight: 1.3,
              }}
              bold={true}
              themeColor="black"
            >
              {"•"}
            </ResumePDFText>
            <ResumePDFText
              style={{
                lineHeight: 1.3,
                flexGrow: 1,
                flexBasis: 0,
                ...(liShouldHighlight ? {
                  backgroundColor: highlight.style.backgroundColor,
                  color: highlight.style.color || highlight.style.textDecorationColor,
                  fontWeight: highlight.style.fontWeight as any,
                  textDecoration: highlight.style.textDecoration as any,
                  textDecorationStyle: highlight.style.textDecorationStyle as any,
                  textDecorationColor: highlight.style.textDecorationColor as any,
                } : {} )
              }}
              themeColor="black"
            >
              {parseAndTransformHTMLWithHighlightBlackText(element.innerHTML, currentSection, currentItemIndex, enableMistakeHighlight, mistakeAnalysis)}
            </ResumePDFText>
          </View>
        );
        
      default:
        return null;
    }
  }
  return null;
};

/**
 * Parse and transform HTML with highlighting support for black text
 */
const parseAndTransformHTMLWithHighlightBlackText = (
  htmlString: string,
  currentSection: string,
  currentItemIndex: number | null,
  enableMistakeHighlight: boolean = false,
  mistakeAnalysis?: ReturnType<typeof useMistakeAnalysis>
): React.ReactNode[] => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, "text/html");
  const transformedChildren: React.ReactNode[] = [];
  const highlight = useHighlight();

  const isTarget = isHighlightTarget(currentSection, currentItemIndex, highlight);

  const transformNode = (node: Node, existingStyles: { bold?: boolean; italic?: boolean; underline?: boolean } = {}) => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as HTMLElement;
      const nodeName = element.nodeName.toLowerCase();

      let newStyles = { ...existingStyles };
      
      switch (nodeName) {
        case "strong":
        case "b":
          newStyles.bold = true;
          return (
            <>
              {Array.from(element.childNodes).map((child, idx) => (
                <React.Fragment key={`${Math.random()}-${idx}`}>
                  {transformNode(child, newStyles)}
                </React.Fragment>
              ))}
            </>
          );
          
        case "em":
        case "i":
          newStyles.italic = true;
          return (
            <>
              {Array.from(element.childNodes).map((child, idx) => (
                <React.Fragment key={`${Math.random()}-${idx}`}>
                  {transformNode(child, newStyles)}
                </React.Fragment>
              ))}
            </>
          );
          
        case "u":
          newStyles.underline = true;
          return (
            <>
              {Array.from(element.childNodes).map((child, idx) => (
                <React.Fragment key={`${Math.random()}-${idx}`}>
                  {transformNode(child, newStyles)}
                </React.Fragment>
              ))}
            </>
          );
          
        case "a":
          const href = element.getAttribute("href");
          return (
            <ResumePDFLink
              isPDF={DEBUG_RESUME_PDF_FLAG as true}
              src={href || "#"}
              key={Math.random()}
            >
              <ResumePDFText themeColor="blue" underline={true}>
                {Array.from(element.childNodes).map((child, idx) => (
                  <React.Fragment key={`${Math.random()}-${idx}`}>
                    {transformNode(child, { ...newStyles, underline: true })}
                  </React.Fragment>
                ))}
              </ResumePDFText>
            </ResumePDFLink>
          );
          
        case "span":
          return (
            <>
              {Array.from(element.childNodes).map((child, idx) => (
                <React.Fragment key={`${Math.random()}-${idx}`}>
                  {transformNode(child, newStyles)}
                </React.Fragment>
              ))}
            </>
          );
          
        default:
          return (
            <>
              {Array.from(element.childNodes).map((child, idx) => (
                <React.Fragment key={`${Math.random()}-${idx}`}>
                  {transformNode(child, newStyles)}
                </React.Fragment>
              ))}
            </>
          );
      }
    } else if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent || "";
      
      if (!text.trim()) return text;
      
      // Check if entire text is a sentence mistake
      const isSentenceError = enableMistakeHighlight && mistakeAnalysis && currentSection && currentItemIndex !== null && 
        isSentenceMistake(text, currentSection, currentItemIndex, mistakeAnalysis.mistakeData);

      if (isSentenceError) {
        // Text is a sentence mistake - handle hover interactions
        const isWordHover = isTarget && highlight.enabled && highlight.mode === 'word' && highlight.texts.length > 0;
        const isSentenceHover = isTarget && highlight.enabled && highlight.mode === 'sentence' && highlight.texts.length > 0 &&
          shouldHighlightText(text, highlight.texts, 'sentence');

        if (isWordHover) {
          // Sentence mistake + word hover
          return renderTextWithWordHighlightsBlackText(
            text, 
            highlight, 
            existingStyles,
            currentSection,
            currentItemIndex,
            mistakeAnalysis,
            true,  // isHoverTarget
            enableMistakeHighlight,
            true   // forceRedText
          );
        } else if (isSentenceHover) {
          // Sentence mistake + sentence hover
          const mergedStyle = getMergedHighlightStyle(existingStyles, highlight.style, true);
          return (
            <ResumePDFText key={Math.random()} style={{ ...mergedStyle, color: MISTAKE_TEXT_COLOR }} themeColor="black">
              {text}
            </ResumePDFText>
          );
        } else {
          // Sentence mistake, no hover
          return (
            <ResumePDFText key={Math.random()} style={{ color: MISTAKE_TEXT_COLOR }} themeColor="black">
              {text}
            </ResumePDFText>
          );
        }
      }

      // Check if text needs word-level mistake processing
      const needsWordProcessing = enableMistakeHighlight && mistakeAnalysis && currentSection && currentItemIndex !== null;
      
      if (!isTarget || !highlight.enabled) {
        if (needsWordProcessing) {
          // Word-level mistake detection without hover
          return renderTextWithWordHighlightsBlackText(
            text,
            highlight,
            existingStyles,
            currentSection,
            currentItemIndex,
            mistakeAnalysis,
            false,  // isHoverTarget
            enableMistakeHighlight,
            false   // forceRedText
          );
        }
        
        if (existingStyles.bold || existingStyles.italic || existingStyles.underline) {
          return (
            <ResumePDFText
              key={Math.random()}
              bold={existingStyles.bold}
              italic={existingStyles.italic}
              underline={existingStyles.underline}
              themeColor="black"
            >
              {text}
            </ResumePDFText>
          );
        }
        return text;
      }

      if (highlight.mode === 'word' && highlight.texts.length > 0) {
        return renderTextWithWordHighlightsBlackText(
          text, 
          highlight, 
          existingStyles,
          currentSection,
          currentItemIndex,
          mistakeAnalysis,
          true,  // isHoverTarget
          enableMistakeHighlight,
          false  // forceRedText
        );
      }

      if (highlight.mode === 'sentence' && highlight.texts.length > 0) {
        const shouldHighlight = shouldHighlightText(text, highlight.texts, 'sentence');
        
        if (needsWordProcessing) {
          // Sentence hover + word-level mistakes
          return renderTextWithWordHighlightsBlackText(
            text,
            highlight,
            existingStyles,
            currentSection,
            currentItemIndex,
            mistakeAnalysis,
            shouldHighlight,  // isHoverTarget
            enableMistakeHighlight,
            false  // forceRedText
          );
        }
        
        return (
          <ResumePDFText
            key={Math.random()}
            style={getMergedHighlightStyle(existingStyles, highlight.style, shouldHighlight)}
            themeColor="black"
          >
            {text}
          </ResumePDFText>
        );
      }

      if (existingStyles.bold || existingStyles.italic || existingStyles.underline) {
        return (
          <ResumePDFText
            key={Math.random()}
            bold={existingStyles.bold}
            italic={existingStyles.italic}
            underline={existingStyles.underline}
            themeColor="black"
          >
            {text}
          </ResumePDFText>
        );
      }
      
      return text;
    }
    
    return null;
  };

  doc.body.childNodes.forEach((node) => {
    const transformed = transformNode(node);
    if (transformed) {
      transformedChildren.push(transformed);
    }
  });

  return transformedChildren;
};

/**
 * Render text with word-level highlights for black text
 */
const renderTextWithWordHighlightsBlackText = (
  text: string,
  highlight: ReturnType<typeof useHighlight>,
  existingStyles: { bold?: boolean; italic?: boolean; underline?: boolean },
  currentSection?: string,
  currentItemIndex?: number | null,
  mistakeAnalysis?: ReturnType<typeof useMistakeAnalysis>,
  isHoverTarget: boolean = false,
  enableMistakeHighlight: boolean = false,
  forceRedText: boolean = false
): React.ReactNode => {
  try {
    const words = highlight.texts
      .map(w => (w || "").trim())
      .filter(Boolean)
      .map(w => {
        const escaped = w.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        return escaped;
      })
      .join("|");

    if (!words) {
      // No hover highlights, check for mistakes only
      if (enableMistakeHighlight && mistakeAnalysis && currentSection && currentItemIndex !== null && !forceRedText) {
        const textWords = text.split(/\s+/).filter(Boolean);
        const hasAnyMistake = textWords.some(word =>
          isSingleWordMistake(word, currentSection, currentItemIndex ?? null, mistakeAnalysis.mistakeData)
        );

        if (hasAnyMistake) {
          return (
            <>
              {textWords.map((word, i) => {
                const isMistake = isSingleWordMistake(word, currentSection, currentItemIndex ?? null, mistakeAnalysis.mistakeData);
                const mergedStyle = isMistake
                  ? { ...existingStyles, color: MISTAKE_TEXT_COLOR }
                  : existingStyles;
                
                return (
                  <React.Fragment key={i}>
                    {i > 0 && " "}
                    {(mergedStyle.bold || mergedStyle.italic || mergedStyle.underline || isMistake) ? (
                      <ResumePDFText
                        bold={mergedStyle.bold}
                        italic={mergedStyle.italic}
                        underline={mergedStyle.underline}
                        style={isMistake ? { color: MISTAKE_TEXT_COLOR } : {}}
                        themeColor="black"
                      >
                        {word}
                      </ResumePDFText>
                    ) : (
                      word
                    )}
                  </React.Fragment>
                );
              })}
            </>
          );
        }
      }
      
      if (forceRedText) {
        return (
          <ResumePDFText key={Math.random()} style={{ color: MISTAKE_TEXT_COLOR }} themeColor="black">
            {text}
          </ResumePDFText>
        );
      }
      
      if (existingStyles.bold || existingStyles.italic || existingStyles.underline) {
        return (
          <ResumePDFText
            key={Math.random()}
            bold={existingStyles.bold}
            italic={existingStyles.italic}
            underline={existingStyles.underline}
            themeColor="black"
          >
            {text}
          </ResumePDFText>
        );
      }
      return text;
    }

    const re = new RegExp(`\\b(${words})\\b`, "giu");
    const parts: Array<{ text: string; match: boolean }> = [];
    let lastIndex = 0;
    let match;

    while ((match = re.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push({ text: text.slice(lastIndex, match.index), match: false });
      }
      parts.push({ text: match[0], match: true });
      lastIndex = re.lastIndex;
    }

    if (lastIndex < text.length) {
      parts.push({ text: text.slice(lastIndex), match: false });
    }

    return (
      <>
        {parts.map((part, i) => {
          const baseStyle = getMergedHighlightStyle(existingStyles, highlight.style, part.match && isHoverTarget);
          
          // Check for word-level mistakes
          const isMistakeWord = !forceRedText && enableMistakeHighlight && mistakeAnalysis && currentSection && currentItemIndex !== null &&
            isSingleWordMistake(part.text, currentSection, currentItemIndex ?? null, mistakeAnalysis.mistakeData);
          
          let finalStyle = baseStyle;
          if (forceRedText) {
            finalStyle = { ...baseStyle, color: MISTAKE_TEXT_COLOR };
          } else if (isMistakeWord) {
            // Mistake word: combine hover highlight (if any) with red text
            if (part.match && isHoverTarget) {
              // Hover + mistake = yellow background + red text
              finalStyle = { ...baseStyle, color: MISTAKE_TEXT_COLOR };
            } else {
              // Mistake only = red text
              finalStyle = { ...baseStyle, color: MISTAKE_TEXT_COLOR };
            }
          }
          
          return (
            <ResumePDFText key={i} style={finalStyle} themeColor="black">
              {part.text}
            </ResumePDFText>
          );
        })}
      </>
    );
  } catch (error) {
    if (forceRedText) {
      return (
        <ResumePDFText key={Math.random()} style={{ color: MISTAKE_TEXT_COLOR }} themeColor="black">
          {text}
        </ResumePDFText>
      );
    }
    
    if (existingStyles.bold || existingStyles.italic || existingStyles.underline) {
      return (
        <ResumePDFText
          key={Math.random()}
          bold={existingStyles.bold}
          italic={existingStyles.italic}
          underline={existingStyles.underline}
          themeColor="black"
        >
          {text}
        </ResumePDFText>
      );
    }
    return text;
  }
};

//  text description component for right column (white background)
export const ResumePDFDescriptionBlackText = ({ 
  items,
  enableHighlight = false,
  enableMistakeHighlight = false,
  section = 'experience',
  itemIndex = 0
}: { 
  items: string[];
  enableHighlight?: boolean;
  enableMistakeHighlight?: boolean;
  section?: string;
  itemIndex?: number | null;
}) => {
  const mistakeAnalysis = useMistakeAnalysis();
  
  if (!items || items.length === 0) {
    return null;
  }
  const newDescription = items.join("");
  const descriptionNodes = extractNodesFromHtml(newDescription);

  const renderNodeWithBlackText = (node: Node): React.ReactNode => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as Element;
      switch (element.nodeName.toLowerCase()) {
        case "p":
          return (
            <View>
              <ResumePDFText
                style={{ lineHeight: 1.3, flexGrow: 1 }}
                themeColor="black"
              >
                {parseAndTransformHTML(element.innerHTML)}
                <br />
              </ResumePDFText>
            </View>
          );
          
        case "ol":
          return (
            <View style={{ ...styles.flexCol }}>
              {Array.from(element.childNodes).map((child, index) =>
                child.nodeName.toLowerCase() === "li" ? (
                  <View key={index} style={{ ...styles.flexRow }}>
                    <ResumePDFText
                      style={{
                        paddingLeft: spacing["1"],
                        paddingRight: spacing["1"],
                        lineHeight: 1.3,
                      }}
                      bold={true}
                      themeColor="black" 
                    >
                      {"•"}
                    </ResumePDFText>
                    <ResumePDFText
                      style={{ lineHeight: 1.3, flexGrow: 1, flexBasis: 0 }}
                      themeColor="black" 
                    >
                      {parseAndTransformHTML((child as Element).innerHTML)}
                    </ResumePDFText>
                  </View>
                ) : null
              )}
            </View>
          );
          
        case "li":
          return (
            <View style={{ ...styles.flexRow }}>
              <ResumePDFText
                style={{
                  paddingLeft: spacing["1"],
                  paddingRight: spacing["1"],
                  lineHeight: 1.3,
                }}
                bold={true}
                themeColor="black"
              >
                {"•"}
              </ResumePDFText>
              <ResumePDFText
                style={{
                  lineHeight: 1.3,
                  flexGrow: 1,
                  flexBasis: 0,
                }}
                themeColor="black"
              >
                {parseAndTransformHTML(element.innerHTML)}
              </ResumePDFText>
            </View>
          );
          
        default:
          return null;
      }
    }
    return null;
  };

  return (
    <View wrap={false}>
      {descriptionNodes.map((node, idx) => (
        <React.Fragment key={idx}>
          {enableHighlight ? renderNodeWithHighlightBlackText(node, section, itemIndex, enableMistakeHighlight, mistakeAnalysis) : renderNodeWithBlackText(node)}
        </React.Fragment>
      ))}
    </View>
  );
};

//single-entry section for left column 
export const TemplateEightLeftColumnSection = ({
  themeColor,
  heading,
  children,
}: {
  themeColor?: string;
  heading?: string;
  children: React.ReactNode;
}) => {
  const { dynamicStyles } = useFontSize();
  const handleClickCallback = useCallback(() => handleClick(heading), [heading]);
  
  return (
    <div
      onClick={handleClickCallback}
      style={{
        cursor: "pointer"
      }}
    >
      <View
        wrap={false} // Prevent orphan sections
        style={{
          ...styles.flexCol,
          gap: spacing["1"], 
          marginTop: spacing["3"], 
        }}
      >
        {heading && (
          <View style={{ ...styles.flexCol, gap: spacing["0.5"] }}>
            <Text
              style={{
                ...dynamicStyles.heading,
                fontWeight: "bold",
                letterSpacing: "0.3pt",
                color: "white", // White text for colored background
              }}
              debug={DEBUG_RESUME_PDF_FLAG}
            >
              {heading}
            </Text>
            {themeColor && (
              <View
                style={{
                  height: "1.25pt",
                  width: "100%",
                  backgroundColor: "white", 
                  marginRight: spacing["3.5"],
                }}
                debug={DEBUG_RESUME_PDF_FLAG}
              />
            )}
          </View>
        )}
        {children && (
          <View
            style={{
              ...styles.flexCol,
              gap: spacing["1"], 
            }}
          >
            {children}
          </View>
        )}
      </View>
    </div>
  );
};

// single-entry section for right column 
export const TemplateEightRightColumnSectionSingle = ({
  themeColor,
  heading,
  children,
}: {
  themeColor?: string;
  heading?: string;
  children: React.ReactNode;
}) => {
  const { dynamicStyles } = useFontSize();
  const handleClickCallback = useCallback(() => handleClick(heading), [heading]);
  
  return (
    <div
      onClick={handleClickCallback}
      style={{
        cursor: "pointer"
      }}
    >
      <View
        wrap={false} 
        style={{
          ...styles.flexCol,
          gap: spacing["1"], 
          marginTop: spacing["3"],
        }}
      >
        {heading && (
          <View style={{ ...styles.flexCol, gap: spacing["0.5"] }}>
            <Text
              style={{
                ...dynamicStyles.heading,
                fontWeight: "bold",
                letterSpacing: "0.3pt",
                color: themeColor || DEFAULT_FONT_COLOR, 
              }}
              debug={DEBUG_RESUME_PDF_FLAG}
            >
              {heading}
            </Text>
            {themeColor && (
              <View
                style={{
                  height: "1.25pt",
                  width: "100%",
                  backgroundColor: themeColor, 
                  marginRight: spacing["3.5"],
                }}
                debug={DEBUG_RESUME_PDF_FLAG}
              />
            )}
          </View>
        )}
        {children && (
          <View
            style={{
              ...styles.flexCol,
              gap: spacing["1"], 
            }}
          >
            {children}
          </View>
        )}
      </View>
    </div>
  );
};
